import React from "react";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import CircularProgress from "@mui/material/CircularProgress";
import { Building, MapPin } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Link } from "react-router-dom";
import SingleSelectNew from "../../components/SelectField/SingleSelectNew";
import SingleSelectSearch from "../../components/SelectField/SelectSearch";
import { logUserActionBackground, USER_ACTION_EVENTS } from "../../services/userActionLog";
import { validateStoredSmsMobile } from "../../utils/phoneValidation";
import {
  COUNTRY_SELECT_OPTIONS,
  DEFAULT_COUNTRY,
  getDefaultTimezone,
  getPhoneCountryCode,
  getRegionLabel,
  getRegionOptions,
  validateRegionForCountry,
} from "../../utils/countryConfig";
import FieldError from "../../components/ui/field-error";
import PhoneNumberField from "../../components/Phone/PhoneNumberField";
import TimezoneSelect from "../../components/Phone/TimezoneSelect";

const RegisterStep2 = (props) => {
  const authInputClassName =
    "pl-40 bg-white text-[#0f172a] border-gray-300 placeholder:text-gray-500 focus-visible:ring-[#2980ba]";

  const {
    formState,
    handleInputChange,
    userRegister,
    industry,
    setErrorMessage,
    errorMessage,
    loading,
    onChangeCheckbox,
    state,
    error,
    peoples,
  } = props;

  const country = formState.country || DEFAULT_COUNTRY;
  const regionOptions = getRegionOptions(country);
  const regionLabel = getRegionLabel(country);

  const handleCountryChange = (val) => {
    handleInputChange(
      {
        country: val,
        phoneCountryCode: getPhoneCountryCode(val),
        timeZone: getDefaultTimezone(val),
        companyState: "",
      },
      "batch"
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateAndGoNext();

    if (Object.keys(newErrors).length === 0) {
      logUserActionBackground({
        userId: "pre-register-step2",
        eventName: USER_ACTION_EVENTS.REGISTER_STEP2,
        email: formState.email?.trim().toLowerCase(),
        displayName: `${formState.firstname?.trim()} ${formState.lastname?.trim()}`,
      });
      userRegister(e);
    }
  };

  const validateAndGoNext = () => {
    const {
      companyName,
      phoneNumber,
      industry,
      peoples,
      companyCity,
      companyState,
      country: formCountry,
      timeZone,
    } = formState;
    const newErrors = {};
    const resolvedCountry = formCountry || DEFAULT_COUNTRY;

    if (!formCountry) newErrors.country = "Country is required.";
    if (!companyName?.trim())
      newErrors.companyName = "Company name is required.";
    if (!phoneNumber?.trim()) newErrors.phoneNumber = "Phone Number is required.";
    else {
      const phoneErr = validateStoredSmsMobile(phoneNumber, {
        allowEmpty: false,
        country: resolvedCountry,
      });
      if (phoneErr) newErrors.phoneNumber = phoneErr;
    }
    if (!timeZone) newErrors.timeZone = "Timezone is required.";
    if (!industry) newErrors.industry = "Industry is required.";
    if (!peoples)
      newErrors.peoples = "Enter the number of peoples in your company.";
    if (!companyCity?.trim()) newErrors.companyCity = "City is required.";
    const regionErr = validateRegionForCountry(companyState, resolvedCountry, {
      allowEmpty: false,
    });
    if (regionErr) newErrors.companyState = regionErr;

    setErrorMessage(newErrors);
    return newErrors;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6 py-4 w-full px-4 sm:px-0">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Business Details</h3>
        <p className="text-sm text-gray-500">
          This information helps us customize your experience.
        </p>

        <div className="space-y-2">
          <label htmlFor="country" className="block text-sm font-medium">
            Country <span className="text-red-500">*</span>
          </label>
          <SingleSelectNew
            value={country}
            placeholder="Select Country"
            name="country"
            className="bg-white"
            onChange={handleCountryChange}
            options={COUNTRY_SELECT_OPTIONS}
            tabIndex={6}
          />
          {errorMessage.country && (
            <FieldError message={errorMessage.country} className="color-danger" />
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="timeZone" className="block text-sm font-medium">
            Timezone <span className="text-red-500">*</span>
          </label>
          <TimezoneSelect
            country={country}
            value={formState.timeZone || ""}
            onChange={(val) => handleInputChange(val, "timeZone")}
            name="timeZone"
            className="bg-white"
            tabIndex={7}
          />
          {errorMessage.timeZone && (
            <FieldError message={errorMessage.timeZone} className="color-danger" />
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="phoneNumber" className="block text-sm font-medium">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <PhoneNumberField
            country={country}
            phoneCountryCode={formState.phoneCountryCode}
            value={formState.phoneNumber || ""}
            onChange={handleInputChange}
            name="phoneNumber"
            id="phoneNumber"
            testId="reg-phone"
            tabIndex={8}
            inputClassName="bg-white text-[#0f172a] border-gray-300"
          />
          {errorMessage.phoneNumber && (
            <FieldError message={errorMessage.phoneNumber} className="color-danger" />
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="company" className="block text-sm font-medium">
            Company Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Building className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              fullWidth={true}
              disableUnderline={true}
              onChange={handleInputChange}
              value={formState.companyName ? formState.companyName : ""}
              className={authInputClassName}
              name="companyName"
              id="companyName"
              maxLength={50}
              placeholder="Company Name"
              tabIndex={9}
              data-testid="reg-company"
            />
          </div>
          {errorMessage.companyName && (
            <FieldError message={errorMessage.companyName} className="color-danger" />
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="companyCity" className="block text-sm font-medium">
              City <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                fullWidth={true}
                disableUnderline={true}
                onChange={handleInputChange}
                value={formState.companyCity ? formState.companyCity : ""}
                className={authInputClassName}
                name="companyCity"
                id="companyCity"
                maxLength={50}
                placeholder="City"
                tabIndex={10}
                data-testid="reg-city"
              />
            </div>
            {errorMessage.companyCity && (
              <FieldError message={errorMessage.companyCity} className="color-danger" />
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="companyState" className="block text-sm font-medium">
              {regionLabel} <span className="text-red-500">*</span>
            </label>
            <SingleSelectNew
              value={formState?.companyState ? formState?.companyState : ""}
              placeholder={`Select ${regionLabel}`}
              name={"companyState"}
              className={"bg-white"}
              onChange={(val) => handleInputChange(val, "companyState")}
              options={regionOptions}
              tabIndex={11}
            />
            {errorMessage.companyState && (
              <FieldError message={errorMessage.companyState} className="color-danger" />
            )}
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="industry" className="block text-sm font-medium">
            Industry <span className="text-red-500">*</span>
          </label>
          <SingleSelectSearch
            value={formState?.industry ? formState?.industry : ""}
            placeholder={"Select Industry"}
            className={"bg-white"}
            name={"industry"}
            onChange={(val) => handleInputChange(val, "industry")}
            options={industry}
            tabIndex={12}
          />
          {errorMessage.industry && (
            <FieldError message={errorMessage.industry} className="color-danger" />
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="companySize" className="block text-sm font-medium">
            Company Size <span className="text-red-500">*</span>
          </label>
          <SingleSelectNew
            value={formState?.peoples ? formState?.peoples : ""}
            placeholder={"How many people in your company"}
            name={"peoples"}
            className={"bg-white"}
            onChange={(val) => handleInputChange(val, "peoples")}
            options={peoples}
            tabIndex={13}
          />
        </div>
        {errorMessage.peoples && (
          <FieldError message={errorMessage.peoples} className="color-danger" />
        )}
      </div>

      <div className="loginbottom">
        <FormControlLabel
          className="d-flex flex-center"
          control={
            <Checkbox
              value="yes"
              color="primary"
              checked={Boolean(state?.terms ? state?.terms : false)}
              onChange={onChangeCheckbox}
              tabIndex={14}
              inputProps={{ "data-testid": "reg-terms" }}
            />
          }
          label={
            <div className="text-center text-sm text-gray-500">
              I confirm that I have read and agree to the{" "}
              <Link
                to="/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-800 font-medium"
              >
                Terms and Conditions
              </Link>
              {" "}and{" "}
              <Link
                to="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-800 font-medium"
              >
                Privacy Policy
              </Link>
              .
            </div>
          }
        />
      </div>
      {error !== "" && error !== "Email is already registered" && (
        <FieldError message={error} className="color-danger" />
      )}
      <div className="pt-4">
        <Button
          type="submit"
          className="w-full"
          size="lg"
          tabIndex={15}
          data-testid="reg-submit-step2"
        >
          {loading ? (
            <CircularProgress size={28} style={{ color: "white" }} />
          ) : (
            "Complete Setup"
          )}
        </Button>
      </div>
    </form>
  );
};

export default RegisterStep2;
